import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectBookingPageSettings,
  selectBookingPageSettingsError,
  selectBookingPageSettingsStatus,
} from "../../features/trial-requests/trial-requests-slice";
import {
  updateBookingPageSettings,
  uploadServiceImage,
} from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { BookingPageServiceContract } from "../../features/trial-requests/contracts/BookingPageServiceContract";
import { toUpdateRequest } from "../../features/trial-requests/utils/bookingPage";
import ServiceEditorRow from "./ServiceEditorRow";

const MAX_SERVICES = 8;

const normalize = (services: BookingPageServiceContract[]): BookingPageServiceContract[] =>
  services
    .map((service) => ({
      title: service.title.trim(),
      description: service.description?.trim() || null,
      priceText: service.priceText?.trim() || null,
      icon: service.icon || null,
      imageUrl: service.imageUrl || null,
    }))
    .filter((service) => service.title !== "");

/**
 * The services/offerings shown on the landing page: title, short description,
 * a free-text price, and an optional curated icon or uploaded picture.
 */
const WebsiteServicesCard = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const status = useAppSelector(selectBookingPageSettingsStatus);
  const error = useAppSelector(selectBookingPageSettingsError);
  const [services, setServices] = useState<BookingPageServiceContract[]>([]);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!settings) return;
    setServices(settings.services);
  }, [settings]);

  const dirty =
    !!settings && JSON.stringify(normalize(services)) !== JSON.stringify(normalize(settings.services));

  const update = (index: number, patch: Partial<BookingPageServiceContract>) => {
    setServices((current) =>
      current.map((service, i) => (i === index ? { ...service, ...patch } : service)),
    );
  };

  const handleUploadPicture = async (index: number, file: File) => {
    setUploadingIndex(index);
    try {
      const { url } = await dispatch(uploadServiceImage(file)).unwrap();
      update(index, { imageUrl: url, icon: null });
    } catch {
      alert(t("website.imageUploadFailed", "Upload failed — please try again."));
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleSave = () => {
    if (!settings) return;
    dispatch(
      updateBookingPageSettings({
        ...toUpdateRequest(settings),
        services: normalize(services),
      }),
    );
  };

  if (!settings?.enabled || settings.layout !== "Landing") {
    return null;
  }

  return (
    <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-10 lg:p-6">
      <h2 className="text-lg font-semibold text-grey-20 dark:text-white">
        {t("website.servicesTitle", "Services")}
      </h2>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("website.servicesIntro", "What you offer, in your words — with an optional price line. Shown as cards on your landing page.")}
      </p>

      {services.map((service, index) => (
        <ServiceEditorRow
          key={index}
          service={service}
          onChange={(patch) => update(index, patch)}
          onRemove={() => setServices((current) => current.filter((_, i) => i !== index))}
          onUploadPicture={(file) => handleUploadPicture(index, file)}
          uploading={uploadingIndex === index}
        />
      ))}

      {services.length < MAX_SERVICES && (
        <button
          type="button"
          className="btn btn-secondary mt-4"
          onClick={() =>
            setServices((current) => [
              ...current,
              { title: "", description: null, priceText: null, icon: null, imageUrl: null },
            ])
          }
        >
          <Plus size={16} />
          {t("website.addService", "Add service")}
        </button>
      )}

      {error && <p className="field-error mt-2">{error}</p>}

      {dirty && (
        <button
          type="button"
          className="btn btn-primary mt-4 ml-2"
          onClick={handleSave}
          disabled={status === "loading" || uploadingIndex !== null}
        >
          {t("website.save", "Save changes")}
        </button>
      )}
    </div>
  );
};

export default WebsiteServicesCard;
